import { useWorkareaPricingInvalidator } from '../../domain/workareas/workareaPricingQueries'
import { notification } from 'antd'
import { UpdateJobItemsMutationData } from './itemModel'
import { GetJobWorkareaItemsResponse, getJobItems, updateJobItems } from './itemsApi'
import { useIsMutating, useMutation, useQuery, useQueryClient } from 'react-query'
import { useWorkareaGroupsInvalidator } from '../../domain/workareas/groupsQueries'

const QueryKeyPrefix = ['workarea-items']
const saveItemsMutationKey = ['save-workarea-items']

export const useIsSavingJobItems = (jobId: number) => useIsMutating({
  mutationKey: saveItemsMutationKey,
  predicate: mutation => (mutation.state.variables as UpdateJobItemsMutationData | undefined)?.jobId === jobId,
}) > 0

export const useJobItems = (jobId: number, onSuccess?: (data: GetJobWorkareaItemsResponse) => void) => {
  return useQuery(
    [...QueryKeyPrefix, { jobId }],
    () => getJobItems(jobId),
    { enabled: jobId !== undefined, onSuccess }
  )
}

export const useJobItemsMutation = () => {
  const queryClient = useQueryClient()
  const workareaGroupsInvalidator = useWorkareaGroupsInvalidator()
  const pricingInvalidator = useWorkareaPricingInvalidator()
  return useMutation({
    mutationKey: saveItemsMutationKey,
    mutationFn: (data: UpdateJobItemsMutationData) => updateJobItems(data.jobId, data.serverRequest),
    onMutate: (data) => {
      queryClient.setQueryData([...QueryKeyPrefix, { jobId: data.jobId }], data.optimisticUpdate)
    },
    onSuccess: (_, data) => {
      queryClient.invalidateQueries([...QueryKeyPrefix, { jobId: data.jobId }])
      workareaGroupsInvalidator.invalidateForJob(data.jobId)
      pricingInvalidator.invalidateForJob(data.jobId)
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'There was a problem saving workarea items.'
      notification.error({ message: `${message} Your last few changes may not have been saved.` })
      queryClient.invalidateQueries(QueryKeyPrefix)
    }
  })
}

export const useJobItemsInvalidator = () => {
  const queryClient = useQueryClient()
  const pricingInvalidator = useWorkareaPricingInvalidator()
  return {
    invalidateForJob: (jobId: number) => Promise.all([
      queryClient.invalidateQueries([...QueryKeyPrefix, { jobId }]),
      pricingInvalidator.invalidateForJob(jobId),
    ])
  }
}